import React, { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { ArrowLeft, Save, Calendar, DollarSign } from 'lucide-react'
import { toast } from 'sonner'
import { projectsService, ProjectUpdate, Project } from '../services/projects'
import { getErrorMessage } from '../utils/apiTransformers'
import { CustomFieldsPanel } from '../components/CustomFieldsPanel'
import { useLocalization } from '../contexts/LocalizationContext'
import { useStatusOptions } from '../hooks/useStatusOptions'
import type { ApiErrorLike } from '../utils/apiError'

export default function EditProject() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const { prefs } = useLocalization()
  // Part 16 — pull status options from the org's set (or canonical fallback).
  const { options: statusOptions } = useStatusOptions('project')
  const [loading, setLoading] = useState(false)
  const [fetching, setFetching] = useState(true)
  const [project, setProject] = useState<Project | null>(null)
  const [formData, setFormData] = useState<ProjectUpdate>({
    name: '',
    description: '',
    status: 'planning',
    priority: 'medium',
    start_date: '',
    end_date: '',
    budget: '',
    currency: '',
    gl_code: '',
    cost_center: '',
  })

  useEffect(() => {
    if (id) fetchProject()
  }, [id])

  const fetchProject = async () => {
    if (!id) return navigate('/project')
    const projectId = parseInt(id)
    if (isNaN(projectId)) {
      toast.error('Invalid project ID')
      return navigate('/project')
    }
    setFetching(true)
    try {
      const data = await projectsService.get(projectId)
      setProject(data)
      setFormData({
        name: data.name,
        description: data.description || '',
        status: data.status,
        priority: data.priority,
        start_date: data.start_date || '',
        end_date: data.end_date || '',
        budget: data.budget || '',
        currency: data.currency || '',
        gl_code: data.gl_code || '',
        cost_center: data.cost_center || '',
      })
    } catch (errorRaw) {
      const error = errorRaw as ApiErrorLike
      toast.error(getErrorMessage(error) || 'Failed to load project')
      navigate('/project')
    } finally {
      setFetching(false)
    }
  }

  const handleChange = (field: keyof ProjectUpdate) => (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    setFormData((prev) => ({ ...prev, [field]: e.target.value }))
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!formData.name?.trim()) {
      toast.error('Project name is required')
      return
    }
    if (formData.start_date && formData.end_date) {
      if (new Date(formData.end_date) < new Date(formData.start_date)) {
        toast.error('End date must be after start date')
        return
      }
    }
    setLoading(true)
    try {
      const projectId = parseInt(id!)
      await projectsService.update(projectId, {
        name: formData.name!.trim(),
        description: formData.description?.trim() || undefined,
        status: formData.status,
        priority: formData.priority,
        start_date: formData.start_date || undefined,
        end_date: formData.end_date || undefined,
        budget: formData.budget || undefined,
        currency: formData.currency || undefined,
        gl_code: formData.gl_code || undefined,
        cost_center: formData.cost_center || undefined,
      })
      toast.success('Project updated successfully!')
      navigate(`/project/${id}`)
    } catch (errorRaw) {
      const error = errorRaw as ApiErrorLike
      toast.error(getErrorMessage(error) || 'Failed to update project')
    } finally {
      setLoading(false)
    }
  }

  if (fetching) {
    return (
      <div className="flex h-screen items-center justify-center bg-[#f0f4f8]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#486581] mx-auto mb-4"></div>
          <p className="text-[#486581]">Loading project...</p>
        </div>
      </div>
    )
  }

  if (!project) return null

  return (
    <div className="p-6 space-y-6 bg-[#f0f4f8] min-h-screen">
      <div className="max-w-4xl mx-auto">
        <div className="bg-gradient-to-r from-[#102a43] via-[#486581] to-[#102a43] rounded-xl p-6 text-white shadow-lg border border-[#829ab1]/20 mb-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl mb-2">Edit Project</h1>
              <p className="text-[#bcccdc]">Update details for {project.name}</p>
            </div>
            <button
              onClick={() => navigate(`/project/${id}`)}
              className="px-4 py-2 bg-[#486581] hover:bg-[#5a7692] border border-[#829ab1] rounded-lg transition-colors flex items-center gap-2"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to Project
            </button>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="bg-white rounded-xl border-2 border-[#bcccdc] shadow-md p-8">
          <div className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-[#102a43] mb-2">
                Project Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={formData.name || ''}
                onChange={handleChange('name')}
                required
                className="w-full px-4 py-2 border-2 border-[#bcccdc] rounded-lg focus:border-[#486581] focus:outline-none text-[#2A3B4C]"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-[#102a43] mb-2">Description</label>
              <textarea
                value={formData.description || ''}
                onChange={handleChange('description')}
                rows={4}
                className="w-full px-4 py-2 border-2 border-[#bcccdc] rounded-lg focus:border-[#486581] focus:outline-none resize-none text-[#2A3B4C]"
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-[#102a43] mb-2">Status</label>
                <select
                  value={formData.status || 'planning'}
                  onChange={handleChange('status')}
                  className="w-full px-4 py-2 border-2 border-[#bcccdc] rounded-lg focus:border-[#486581] focus:outline-none text-[#2A3B4C]"
                >
                  {statusOptions.map((o) => (
                    <option key={o.key} value={o.key}>
                      {o.label}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-[#102a43] mb-2">Priority</label>
                <select
                  value={formData.priority || 'medium'}
                  onChange={handleChange('priority')}
                  className="w-full px-4 py-2 border-2 border-[#bcccdc] rounded-lg focus:border-[#486581] focus:outline-none text-[#2A3B4C]"
                >
                  <option value="low">Low</option>
                  <option value="medium">Medium</option>
                  <option value="high">High</option>
                  <option value="urgent">Urgent</option>
                </select>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-[#102a43] mb-2">
                  <Calendar className="w-4 h-4 inline mr-1" />
                  Start Date
                </label>
                <input
                  type="date"
                  value={formData.start_date || ''}
                  onChange={handleChange('start_date')}
                  className="w-full px-4 py-2 border-2 border-[#bcccdc] rounded-lg focus:border-[#486581] focus:outline-none text-[#2A3B4C]"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-[#102a43] mb-2">
                  <Calendar className="w-4 h-4 inline mr-1" />
                  End Date
                </label>
                <input
                  type="date"
                  value={formData.end_date || ''}
                  onChange={handleChange('end_date')}
                  min={formData.start_date || undefined}
                  className="w-full px-4 py-2 border-2 border-[#bcccdc] rounded-lg focus:border-[#486581] focus:outline-none text-[#2A3B4C]"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-[#102a43] mb-2">
                  <DollarSign className="w-4 h-4 inline mr-1" />
                  Budget
                </label>
                <input
                  type="number"
                  value={formData.budget || ''}
                  onChange={handleChange('budget')}
                  placeholder="0.00"
                  step="0.01"
                  min="0"
                  className="w-full px-4 py-2 border-2 border-[#bcccdc] rounded-lg focus:border-[#486581] focus:outline-none text-[#2A3B4C]"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-[#102a43] mb-2">
                  Currency (ISO 4217) <span className="text-xs text-[#556B7C]">— blank inherits org default</span>
                </label>
                <input
                  type="text"
                  maxLength={3}
                  value={formData.currency || ''}
                  onChange={handleChange('currency')}
                  placeholder={prefs.currency}
                  className="w-full px-4 py-2 border-2 border-[#bcccdc] rounded-lg focus:border-[#486581] focus:outline-none text-[#2A3B4C] uppercase"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-[#102a43] mb-2">GL Code</label>
                <input
                  type="text"
                  value={formData.gl_code || ''}
                  onChange={handleChange('gl_code')}
                  placeholder="e.g. 5100-CapEx"
                  className="w-full px-4 py-2 border-2 border-[#bcccdc] rounded-lg focus:border-[#486581] focus:outline-none text-[#2A3B4C]"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-[#102a43] mb-2">Cost Center</label>
                <input
                  type="text"
                  value={formData.cost_center || ''}
                  onChange={handleChange('cost_center')}
                  placeholder="e.g. OPS-North-Region"
                  className="w-full px-4 py-2 border-2 border-[#bcccdc] rounded-lg focus:border-[#486581] focus:outline-none text-[#2A3B4C]"
                />
              </div>
            </div>

            {project && (
              <div className="pt-6 border-t border-[#f0f4f8]">
                <CustomFieldsPanel
                  entityType="project"
                  entityId={project.id}
                />
              </div>
            )}

            <div className="flex items-center justify-between pt-6 border-t border-[#f0f4f8]">
              <button
                type="button"
                onClick={() => navigate(`/project/${id}`)}
                className="px-6 py-2 border-2 border-[#bcccdc] text-[#486581] rounded-lg hover:bg-[#f0f4f8] transition-colors"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={loading || !formData.name?.trim()}
                className="px-8 py-2 bg-gradient-to-r from-[#DAA520] to-[#FFD700] text-[#102a43] rounded-lg hover:from-[#e58900] hover:to-[#c86600] transition-colors font-medium flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-[#102a43]"></div>
                    Saving...
                  </>
                ) : (
                  <>
                    <Save className="w-4 h-4" />
                    Save Changes
                  </>
                )}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  )
}
